/**
 * Envelope history — documents, every recipient and DocuSign's audit trail
 * (GET /contracts/envelopes/{id}/timeline). Answers "when was it sent / viewed /
 * signed, and by whom" without leaving the portal.
 */
import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Loader2, FileText, Users, History } from "lucide-react";
import { getEnvelopeTimeline } from "@/lib/api";
import { fmtDate, STATUS_TONE } from "./envelopeUtils";

const KIND_LABELS = { signers: "Signer", witnesses: "Witness", carbonCopies: "CC", certifiedDeliveries: "Certified delivery", inPersonSigners: "In-person signer", agents: "Agent", editors: "Editor", intermediaries: "Intermediary", notaries: "Notary" };

const Section = ({ icon: Icon, title, children }) => (
  <div className="space-y-2">
    <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
      <Icon className="w-3.5 h-3.5" /> {title}
    </h4>
    {children}
  </div>
);

export const EnvelopeHistoryDrawer = ({ envelope, open, onClose, locale }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !envelope?.envelope_id) return;
    setData(null); setError(null); setLoading(true);
    getEnvelopeTimeline(envelope.envelope_id)
      .then(setData)
      .catch((e) => setError(e?.response?.data?.detail || "Could not load the envelope history"))
      .finally(() => setLoading(false));
  }, [open, envelope?.envelope_id]);

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto" data-testid="envelope-history-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2 pr-6">
            <span className="truncate">{envelope?.subject || "Envelope"}</span>
            {data?.status && <Badge className={`shrink-0 ${STATUS_TONE[data.status] || "bg-slate-500 text-white"}`}>{data.status}</Badge>}
          </SheetTitle>
          <SheetDescription className="font-mono text-xs">{envelope?.envelope_id}</SheetDescription>
        </SheetHeader>

        {loading && <div className="flex items-center gap-2 py-10 justify-center text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin" /> Loading history…</div>}
        {error && <p className="text-sm text-rose-700 py-6" data-testid="envelope-history-error">{error}</p>}

        {data && (
          <div className="mt-5 space-y-6">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
              {[["Created", data.created_at], ["Sent", data.sent_at], ["Delivered", data.delivered_at], ["Completed", data.completed_at]].map(([k, v]) => (
                <div key={k} className="rounded-md border bg-slate-50 px-2.5 py-2">
                  <p className="text-muted-foreground">{k}</p>
                  <p className="font-medium">{fmtDate(v, locale, true)}</p>
                </div>
              ))}
            </div>
            {data.sender && <p className="text-xs text-muted-foreground">Sent by <span className="font-medium text-foreground">{data.sender}</span></p>}

            <Section icon={FileText} title={`Documents (${data.documents?.length || 0})`}>
              <ul className="divide-y rounded-md border text-sm">
                {(data.documents || []).map((d) => (
                  <li key={d.id} className="flex items-center justify-between px-3 py-2">
                    <span className="truncate">{d.name}</span>
                    <span className="text-xs text-muted-foreground shrink-0 ml-3">{d.pages ? `${d.pages} pp` : d.type}</span>
                  </li>
                ))}
              </ul>
            </Section>

            <Section icon={Users} title={`Recipients (${data.recipients?.length || 0})`}>
              <ul className="divide-y rounded-md border text-sm">
                {(data.recipients || []).map((r, i) => (
                  <li key={i} className="px-3 py-2 grid grid-cols-12 gap-2 items-center">
                    <span className="col-span-1 text-xs text-muted-foreground">#{r.routing_order}</span>
                    <span className="col-span-5 min-w-0">
                      <span className="block truncate font-medium">{r.name || "—"}</span>
                      <span className="block truncate text-xs text-muted-foreground">{r.email}</span>
                    </span>
                    <span className="col-span-2 text-xs">{KIND_LABELS[r.kind] || r.kind} · {r.status}</span>
                    <span className="col-span-4 text-xs text-muted-foreground text-right">
                      {r.signed_at ? `Signed ${fmtDate(r.signed_at, locale, true)}` : r.delivered_at ? `Viewed ${fmtDate(r.delivered_at, locale, true)}` : r.sent_at ? `Sent ${fmtDate(r.sent_at, locale, true)}` : "Not yet sent"}
                      {r.declined_reason && <span className="block text-rose-700">Declined: {r.declined_reason}</span>}
                    </span>
                  </li>
                ))}
              </ul>
            </Section>

            <Section icon={History} title={`Audit trail (${data.audit?.length || 0})`}>
              <ol className="relative border-l border-slate-200 ml-2 space-y-3" data-testid="envelope-audit-list">
                {(data.audit || []).map((a, i) => (
                  <li key={i} className="ml-4">
                    <span className="absolute -left-1.5 mt-1.5 w-3 h-3 rounded-full bg-sky-500 border-2 border-white" />
                    <p className="text-xs text-muted-foreground">{fmtDate(a.at, locale, true)}{a.by ? ` · ${a.by}` : ""}</p>
                    <p className="text-sm"><span className="font-medium">{a.action}</span>{a.message ? ` — ${a.message}` : ""}</p>
                  </li>
                ))}
              </ol>
            </Section>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
